import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { User } from "../../../../../api/accessControl/User.ts";
import { BackendApi } from "../../../../../api/api.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import { PrimaryButton } from "../../../../../components/general/PrimaryButton.tsx";
import Popup from "../../../../../components/general/Popup.tsx";
import { SecondaryButton } from "../../../../../components/general/SecondaryButton.tsx";
import WideDrawer from "../../../../../components/general/WideDrawer.tsx";
import { formatPeriodLabel } from "../../../../../components/general/DateRangeInput/utils/dateRangeUtils.ts";
import { useNotifications } from "../../../../../lib/NotificationContext.tsx";
import { isAbortError } from "../../../../../lib/abortError.ts";
import { vatDeclarationAnswerMessage } from "../../../../../lib/apiErrors.ts";
import { getCurrentBusiness, logInWithBusinessAt } from "../../../../../utils/OrgManagement.ts";
import { DocumentPreview } from "../../../../../views/DocumentDrawer/components/DocumentPreview.tsx";
import type { DocumentContent } from "../../../../../views/DocumentDrawer/components/DocumentPreview.tsx";
import { RejectVatDeclarationPopup } from "./components/RejectVatDeclarationPopup.tsx";
import { VatDeclarationApprovalOutcome } from "./components/VatDeclarationApprovalOutcome.tsx";
import { VatDeclarationForm } from "../../../../../components/specific/VatDeclaration/index.ts";
import { useVatDeclarationApprovalRequest } from "./useVatDeclarationApprovalRequest.ts";
import { formatDateTime } from "../../../../../lib/utils.ts";

const SWITCHED_BUSINESS_PARAM = "switched";
const RELOAD_FAILED_NOTIFICATION = {
    title: "Status niet vernieuwd",
    description: "Je antwoord is opgeslagen, maar de pagina kon niet worden vernieuwd. Herlaad de pagina om de nieuwe status te zien.",
    type: "error",
} as const;

export function VatDeclarationApprovalPage({ currentUser }: { currentUser: User }) {
    const { requestId } = useParams<{ requestId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { dispatch } = useNotifications();
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);
    const { approvalRequest, loadError, retry, isAnswering, approve, reject, loadDocument } =
        useVatDeclarationApprovalRequest(api, requestId);
    const [approvePopupRequestId, setApprovePopupRequestId] = useState<string | null>(null);
    const [rejectPopupRequestId, setRejectPopupRequestId] = useState<string | null>(null);
    const [previewTitle, setPreviewTitle] = useState<string | null>(null);
    const [previewDocument, setPreviewDocument] = useState<DocumentContent | null>(null);
    const previewRequestRef = useRef<AbortController | null>(null);

    useEffect(() => () => previewRequestRef.current?.abort(), []);

    const currentBusinessId = currentUser.businesses[0];
    const isLatestRequest = approvalRequest !== null && approvalRequest.latest_request_id === approvalRequest.id;
    const isOnRequestBusiness = approvalRequest !== null && approvalRequest.business_id === currentBusinessId;
    const hasBusinessSwitchFailed = isLatestRequest && !isOnRequestBusiness && searchParams.has(SWITCHED_BUSINESS_PARAM);
    const answerableRequestId = approvalRequest !== null
        && approvalRequest.id === requestId
        && approvalRequest.status === "pending"
        && approvalRequest.is_latest_version
        ? approvalRequest.id
        : null;
    const isApprovePopupOpen = answerableRequestId !== null && approvePopupRequestId === answerableRequestId;
    const isRejectPopupOpen = answerableRequestId !== null && rejectPopupRequestId === answerableRequestId;

    useEffect(() => {
        if (!approvalRequest) {
            return;
        }
        if (!isLatestRequest) {
            navigate(`/vat-declaration-approval/${approvalRequest.latest_request_id}`, { replace: true });
            return;
        }
        if (isOnRequestBusiness) {
            if (searchParams.has(SWITCHED_BUSINESS_PARAM)) {
                navigate(location.pathname, { replace: true });
            }
            return;
        }
        if (searchParams.has(SWITCHED_BUSINESS_PARAM)) {
            return;
        }
        logInWithBusinessAt(approvalRequest.business_id, `${location.pathname}?${SWITCHED_BUSINESS_PARAM}=1`);
    }, [approvalRequest, isLatestRequest, isOnRequestBusiness, location.pathname, navigate, searchParams]);

    function switchToRequestBusinessIfChanged(): boolean {
        const selectedBusinessId = getCurrentBusiness() ?? currentBusinessId;
        if (!approvalRequest || selectedBusinessId === approvalRequest.business_id) {
            return false;
        }
        logInWithBusinessAt(approvalRequest.business_id, `${location.pathname}?${SWITCHED_BUSINESS_PARAM}=1`);
        return true;
    }

    async function handleApprove() {
        setApprovePopupRequestId(null);
        if (switchToRequestBusinessIfChanged()) {
            return;
        }
        try {
            const isReloaded = await approve();
            dispatch({
                title: "BTW-aangifte goedgekeurd",
                description: "Je boekhouder kan de aangifte nu indienen.",
                type: "success",
            });
            if (!isReloaded) {
                dispatch(RELOAD_FAILED_NOTIFICATION);
            }
        } catch (error) {
            const expectedMessage = vatDeclarationAnswerMessage(error);
            if (expectedMessage === null) {
                Sentry.captureException(error);
            }
            dispatch({
                title: "Goedkeuren is niet gelukt",
                description: expectedMessage ?? "De aanvraag is opnieuw geladen. Controleer de status en probeer het zo nodig opnieuw.",
                type: "error",
            });
        }
    }

    async function handleReject(reason: string) {
        if (switchToRequestBusinessIfChanged()) {
            return;
        }
        try {
            const isReloaded = await reject(reason);
            setRejectPopupRequestId(null);
            dispatch({
                title: "BTW-aangifte afgewezen",
                description: "Je reden is gedeeld met je boekhouder.",
                type: "success",
            });
            if (!isReloaded) {
                dispatch(RELOAD_FAILED_NOTIFICATION);
            }
        } catch (error) {
            const expectedMessage = vatDeclarationAnswerMessage(error);
            if (expectedMessage === null) {
                Sentry.captureException(error);
            }
            dispatch({
                title: "Afwijzen is niet gelukt",
                description: expectedMessage ?? "De aanvraag is opnieuw geladen. Controleer de status en probeer het zo nodig opnieuw.",
                type: "error",
            });
        }
    }

    async function handleDocumentClick(documentId: string, fileName: string | null) {
        if (switchToRequestBusinessIfChanged()) {
            return;
        }
        previewRequestRef.current?.abort();
        const controller = new AbortController();
        previewRequestRef.current = controller;
        setPreviewTitle(fileName ?? "Document");
        setPreviewDocument(null);
        try {
            const content = await loadDocument(documentId, fileName, controller.signal);
            if (!controller.signal.aborted) {
                setPreviewDocument(content);
            }
        } catch (error) {
            if (isAbortError(error) || controller.signal.aborted) {
                return;
            }
            Sentry.captureException(error);
            setPreviewTitle(null);
            dispatch({
                title: "Fout opgetreden",
                description: "Het document kon niet worden geladen.",
                type: "error",
            });
        }
    }

    function closePreview() {
        previewRequestRef.current?.abort();
        setPreviewTitle(null);
        setPreviewDocument(null);
    }

    function renderContent() {
        if (loadError === "failed") {
            return (
                <div className="space-y-4 rounded-md border border-gray-200 bg-white p-6">
                    <p role="alert" className="text-sm text-gray-700">
                        Er ging iets mis bij het laden van de aanvraag. Probeer het opnieuw.
                    </p>
                    <PrimaryButton title="Opnieuw proberen" onClick={retry} />
                </div>
            );
        }
        if (loadError === "not_found" || hasBusinessSwitchFailed) {
            return (
                <div className="space-y-4 rounded-md border border-gray-200 bg-white p-6">
                    <p role="alert" className="text-sm text-gray-700">
                        Deze aanvraag kon niet worden geladen. Controleer of je bent ingelogd met het account waarop de aanvraag is verstuurd.
                    </p>
                    <SecondaryButton title="Naar het dashboard" onClick={() => navigate("/")} />
                </div>
            );
        }
        if (!approvalRequest || approvalRequest.id !== requestId || !isLatestRequest || !isOnRequestBusiness) {
            return <p role="status" className="py-6 text-center text-sm text-gray-500">Laden...</p>;
        }

        const period = formatPeriodLabel(approvalRequest.period_start, approvalRequest.period_end);
        return (
            <div className="w-full max-w-[900px] space-y-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="space-y-1 text-sm text-gray-700">
                        <p className="text-base font-semibold text-gray-900">
                            {approvalRequest.requested_by_name ?? "Je boekhouder"} vraagt je de BTW-aangifte over {period} goed te keuren.
                        </p>
                        <p>Verstuurd op {formatDateTime(approvalRequest.requested_at)}</p>
                    </div>
                    {answerableRequestId !== null && (
                        <div className="flex items-center gap-3">
                            <PrimaryButton
                                title="Afwijzen"
                                icon={XMarkIcon}
                                variant="danger"
                                onClick={() => {
                                    if (!switchToRequestBusinessIfChanged()) {
                                        setRejectPopupRequestId(answerableRequestId);
                                    }
                                }}
                                disabled={isAnswering}
                            />
                            <PrimaryButton
                                title="Goedkeuren"
                                icon={CheckIcon}
                                variant="success"
                                onClick={() => {
                                    if (!switchToRequestBusinessIfChanged()) {
                                        setApprovePopupRequestId(answerableRequestId);
                                    }
                                }}
                                disabled={isAnswering}
                            />
                        </div>
                    )}
                </div>
                <VatDeclarationApprovalOutcome approvalRequest={approvalRequest} />
                <VatDeclarationForm
                    businessId={approvalRequest.business_id}
                    vatDeclaration={approvalRequest}
                    onDocumentClick={handleDocumentClick}
                />
            </div>
        );
    }

    return (
        <Shell currentUser={currentUser} title="BTW-aangifte goedkeuren">
            {renderContent()}
            {approvalRequest && (
                <Popup
                    open={isApprovePopupOpen}
                    onClose={() => setApprovePopupRequestId(null)}
                    onConfirm={() => {
                        if (!isAnswering) {
                            handleApprove();
                        }
                    }}
                    title="BTW-aangifte goedkeuren"
                    confirmButtonDisplayLabel="Goedkeuren"
                    confirmButtonDisabled={isAnswering}
                >
                    <p className="mt-2 text-sm text-gray-700">
                        Je keurt de BTW-aangifte over {formatPeriodLabel(approvalRequest.period_start, approvalRequest.period_end)} goed. Je boekhouder kan deze daarna indienen.
                    </p>
                </Popup>
            )}
            <RejectVatDeclarationPopup
                open={isRejectPopupOpen}
                onClose={() => setRejectPopupRequestId(null)}
                onReject={handleReject}
                isRejecting={isAnswering}
            />
            <WideDrawer
                open={previewTitle !== null}
                updateOpenState={(open: boolean) => {
                    if (!open) {
                        closePreview();
                    }
                }}
                title={previewTitle ?? "Document"}
            >
                {previewDocument ? (
                    <DocumentPreview document={previewDocument} />
                ) : (
                    <p role="status" className="py-6 text-center text-sm text-gray-500">Laden...</p>
                )}
            </WideDrawer>
        </Shell>
    );
}
